// Som de novo pedido tocado DENTRO do painel — complementa o push do Android
// (que toca com o app fechado mas usa o som do SO, sem customizacao). Aqui o
// audio e nosso: toca em loop ate o lojista confirmar (toque/tecla) ou ate um
// teto de seguranca de 90s, pra nao tocar pra sempre se ninguem estiver perto.

export type OrderSoundMode = "off" | "once" | "loop";

const SOUND_URL = `${import.meta.env.BASE_URL}sounds/new-order.mp3`;
const MODE_KEY = "vnz:order-sound-mode";
const LEGACY_ENABLED_KEY = "vnz:order-sound-enabled";
const MAX_RING_MS = 90_000;

let audio: HTMLAudioElement | null = null;
let fonte: "silencio" | "campainha" | null = null;
let unlocked = false;
let silencioUrl: string | null = null;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
let stopOnGesture: (() => void) | null = null;

function getAudio(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio();
    audio.preload = "auto";
  }
  return audio;
}

// 0,1 s de silêncio (WAV PCM 8 bits, mono, 8 kHz; amostra 128 = zero), gerado aqui para não depender de rede.
function silencio(): string {
  if (!silencioUrl) {
    const n = 800;
    const buf = new ArrayBuffer(44 + n);
    const v = new DataView(buf);
    const txt = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
    txt(0, "RIFF");
    v.setUint32(4, 36 + n, true);
    txt(8, "WAVEfmt ");
    v.setUint32(16, 16, true);
    v.setUint16(20, 1, true);
    v.setUint16(22, 1, true);
    v.setUint32(24, 8000, true);
    v.setUint32(28, 8000, true);
    v.setUint16(32, 1, true);
    v.setUint16(34, 8, true);
    txt(36, "data");
    v.setUint32(40, n, true);
    new Uint8Array(buf, 44).fill(128);
    silencioUrl = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
  }
  return silencioUrl;
}

// A campainha só entra no elemento na hora de tocar de verdade.
function comCampainha(a: HTMLAudioElement): void {
  if (fonte !== "campainha") {
    a.src = SOUND_URL;
    fonte = "campainha";
  }
  // Só aqui o som é ligado, com o elemento parado (a troca de fonte o para); o silêncio do destrave toca no mudo.
  a.muted = false;
}

// Autoplay policy: o navegador bloqueia play() ate o primeiro gesto do usuario. O elemento tocado num gesto fica
// liberado para tocar depois sem gesto, mesmo com outra fonte e mesmo tendo tocado no mudo (o WebKit tira a trava do
// elemento no play feito dentro do gesto): destrava tocando o SILÊNCIO nele, no mudo. Tocar a campainha no mudo e tirar
// o mudo logo depois deixava escapar o começo dela no iPhone ao abrir o painel (consulta #455), e tocar sem mudo, mesmo
// silêncio, faria o iPhone parar a música do lojista. Quem chama garante que é um gesto (o play no mudo sempre dá
// certo e não prova nada fora dele); se falhar, a próxima interação tenta de novo.
export function unlockOrderSound(): Promise<boolean> {
  if (unlocked) return Promise.resolve(true);
  const a = getAudio();
  // Já tocando (navegador que deixou tocar sem gesto): está liberado, e trocar a fonte cortaria a campainha.
  if (!a.paused) {
    unlocked = true;
    return Promise.resolve(true);
  }
  if (fonte !== "silencio") {
    a.src = silencio();
    fonte = "silencio";
  }
  a.muted = true;
  a.loop = false;
  return a.play().then(
    () => {
      unlocked = true;
      // Deixa a campainha no cache para o primeiro toque não esperar a rede.
      fetch(SOUND_URL).catch(() => {});
      return true;
    },
    () => false,
  );
}

function disarm(): void {
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
  if (stopOnGesture) {
    window.removeEventListener("pointerdown", stopOnGesture);
    window.removeEventListener("keydown", stopOnGesture);
    stopOnGesture = null;
  }
}

// loop=true ("parar com clique"): toca em loop ate gesto/teto de 90s.
// loop=false ("tocar 1 vez"): toca uma vez e para sozinho.
export function playNewOrderSound(loop: boolean): void {
  const a = getAudio();
  // Re-arma do zero: novo pedido enquanto ja toca reinicia o teto e o gesto.
  disarm();
  comCampainha(a);
  a.loop = loop;
  a.currentTime = 0;
  a.play().catch(() => {});

  if (loop) {
    stopOnGesture = () => stopOrderSound();
    window.addEventListener("pointerdown", stopOnGesture, { once: true });
    window.addEventListener("keydown", stopOnGesture, { once: true });
    safetyTimer = setTimeout(stopOrderSound, MAX_RING_MS);
  }
}

export function stopOrderSound(): void {
  disarm();
  if (!audio) return;
  audio.pause();
  audio.loop = false;
  audio.currentTime = 0;
}

// Toca uma vez (botao "Testar som"), sem loop nem stop-on-gesture. O proprio
// clique no botao ja e o gesto que satisfaz a autoplay policy.
export function previewOrderSound(): void {
  const a = getAudio();
  comCampainha(a);
  a.loop = false;
  a.currentTime = 0;
  a.play().then(
    () => {
      unlocked = true;
    },
    () => {},
  );
}

export function getOrderSoundMode(): OrderSoundMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === "off" || v === "once" || v === "loop") return v;
    // Migracao do toggle booleano anterior: quem tinha desligado continua off.
    if (localStorage.getItem(LEGACY_ENABLED_KEY) === "0") return "off";
  } catch {
    /* localStorage indisponivel (modo privado): cai no default */
  }
  return "once";
}

export function setOrderSoundMode(mode: OrderSoundMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}
